import { ChevronRight as ChevronRightIcon, Info as InfoIcon, MessagesSquare as MessagesSquareIcon } from "@tamagui/lucide-icons";
import { Badge, type BadgeSize, Body2, CalendarText, Card, Icon, RowLayout, SkeletonBlock, SkeletonText, Subtitle1, styled, useMedia, XStack, YStack } from "@medbillai/ui";
import { formatCurrency } from "@medbillai/utils/currency";
import { CaseSummary, type CaseSummaryOutcome } from "./CaseSummary";

// A demoted closed-win: the row shows a "Resolved" badge with a CaseSummary
// result banner embedded below.
export type CaseCardResolvedWin = {
  outcome: CaseSummaryOutcome;
  savings?: number;
  onReview: () => void;
};

// Minimal case shape the card renders — narrowed so callers and test fixtures
// don't need the full query type.
export type CaseCardCase = {
  id: string;
  title?: string | null;
  lastEventSentAt?: string | null;
  /** Bill amount, when known. */
  amount?: number | null;
  /** Imperative label for what the user must do; absent when nothing is blocked. */
  actionLabel?: string;
  /** Unread staff chat; an action label wins when both apply. */
  hasNewMessage?: boolean;
  /** Present on a demoted closed-win; drives the Resolved badge + summary. */
  resolvedWin?: CaseCardResolvedWin;
};
export type CaseCardProps = {
  loading?: false;
  case: CaseCardCase;
  onPress?: () => void;
} | {
  loading: true;
  case?: never;
  onPress?: never;
};

// Warning badge naming the required action, info for an unread staff message,
// neutral "In progress" otherwise, positive "Resolved" for a demoted win.
function StatusBadge({
  win,
  actionLabel,
  hasNewMessage,
  size
}: {
  win: boolean;
  actionLabel?: string;
  hasNewMessage?: boolean;
  size: BadgeSize;
}) {
  if (win) {
    return <Badge status="positive" size={size}>
        Resolved
      </Badge>;
  }
  if (actionLabel) {
    return <Badge status="warning" size={size} iconLeft={InfoIcon}>
        {actionLabel}
      </Badge>;
  }
  return hasNewMessage ? <Badge status="info" size={size} iconLeft={MessagesSquareIcon} data-sentry-element="Badge" data-sentry-component="StatusBadge" data-sentry-source-file="CaseCard.tsx">
      New message
    </Badge> : <Badge status="neutral" size={size} data-sentry-element="Badge" data-sentry-component="StatusBadge" data-sentry-source-file="CaseCard.tsx">
      In progress
    </Badge>;
}
const MetaText = styled(Body2, {
  color: "$textSubtle"
});

// Last activity and bill amount on one line. The date goes through CalendarText
// so the card reads the same as the case list — a time today, "Yesterday",
// "Last Tuesday", then MM/DD/YY.
function CaseMeta({
  date,
  amount
}: {
  date?: string | null;
  amount?: string;
}) {
  return <XStack alignItems="center" data-sentry-element="XStack" data-sentry-component="CaseMeta" data-sentry-source-file="CaseCard.tsx">
      {date ? <CalendarText date={date} element={MetaText} /> : null}
      {amount ? <MetaText>{date ? ` • ${amount}` : amount}</MetaText> : null}
    </XStack>;
}
export function CaseCard(props: CaseCardProps) {
  const {
    loading = false,
    case: caseItem,
    onPress
  } = props;
  const win = caseItem?.resolvedWin;
  const media = useMedia();
  // Narrow screens stack the badge above the title — the action labels are too
  // long to share the title row.
  const badgeAbove = !media.gtXs;
  const date = caseItem?.lastEventSentAt;
  const amount = caseItem?.amount != null ? formatCurrency(caseItem.amount, "USD", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }) : undefined;
  const badge = loading ? <SkeletonBlock width={110} height="$si3" radius="$brFull" /> : <StatusBadge win={win != null} actionLabel={caseItem?.actionLabel} hasNewMessage={caseItem?.hasNewMessage} size={badgeAbove ? "small" : "medium"} />;
  // No chevron on a resolved win — the whole card isn't tappable, only its
  // Review button.
  const chevron = win ? null : <Icon icon={ChevronRightIcon} size="$iconLarge" color="$iconDefault" />;
  const row = <RowLayout primary={<SkeletonText loading={loading} chars={18}>
          <Subtitle1 color="$textDefault" flexShrink={1} numberOfLines={1}>
            {caseItem?.title ?? ""}
          </Subtitle1>
        </SkeletonText>} secondary={loading ? <SkeletonText loading chars={10}>
            <Body2 color="$textSubtle" />
          </SkeletonText> : date || amount ? <CaseMeta date={date} amount={amount} /> : undefined}
  // Wide screens keep badge + chevron in the outer trailing slot so they
  // center against the content block; stacked, the chevron moves out to
  // the card row below so it centers against the badge too.
  rowTrailing={badgeAbove ? undefined : <XStack ai="center" gap="$sp1.5">
            {badge}
            {chevron}
          </XStack>} />;

  // A resolved win embeds the result banner below the row (no whole-card press —
  // the Review button owns the interaction); other states tap to open.
  return <Card variant="outlined" size="large" testID={caseItem ? `your-cases.card-${caseItem.id}` : "your-cases.card-skeleton"} onPress={win || loading ? undefined : onPress} aria-busy={loading || undefined} data-sentry-element="Card" data-sentry-component="CaseCard" data-sentry-source-file="CaseCard.tsx">
      <YStack gap={win ? "$sp2" : "$sp0"} data-sentry-element="YStack" data-sentry-source-file="CaseCard.tsx">
        {badgeAbove ? <XStack alignItems="center" gap="$sp1.5">
            <YStack flexGrow={1} flexShrink={1} gap="$sp1.5">
              <XStack>{badge}</XStack>
              {row}
            </YStack>
            {chevron}
          </XStack> : row}
        {win && caseItem ? <CaseSummary testID={`your-cases.summary-${caseItem.id}`} outcome={win.outcome} savings={win.savings} onReview={win.onReview} /> : null}
      </YStack>
    </Card>;
}